package fyi.moko.hotpl.data.network

import fyi.moko.hotpl.domain.model.PlaceCategory

/**
 * Translates a directory provider's own place-type vocabulary into ordinary HOTPL tags.
 *
 * Directory import used to discard this entirely (`tags = emptyList()`), so every place
 * imported from search arrived with nothing for
 * [fyi.moko.hotpl.domain.model.PlaceCategory.fromTags] to read — the Journal could never
 * tell an imported cafe from an imported bar. 여기서 만드는 값은 숨은 메타데이터가 아니라
 * 사용자가 직접 보고 지울 수 있는 평범한 태그다: 추론이 틀렸으면 태그를 고치면 분류도
 * 따라 바뀐다.
 *
 * Deliberately lossy — only the types that map onto a [fyi.moko.hotpl.domain.model.PlaceCategory]
 * keyword (plus the cuisine word that comes free with Google's `{cuisine}_restaurant`
 * types) are kept. [MAX_TAGS] caps the result so an import can't bury the user's own
 * tags under provider taxonomy.
 */
internal const val MAX_TAGS = 3

/** Google Places (v1) type → the tags it should contribute, most specific first. */
private val GOOGLE_TYPE_TAGS: Map<String, List<String>> = mapOf(
    "cafe" to listOf("cafe"),
    "coffee_shop" to listOf("cafe", "coffee"),
    "tea_house" to listOf("tea"),
    "bakery" to listOf("bakery"),
    "dessert_shop" to listOf("dessert"),
    "dessert_restaurant" to listOf("dessert"),
    "ice_cream_shop" to listOf("dessert"),
    "donut_shop" to listOf("donut"),
    "chocolate_shop" to listOf("dessert"),
    "candy_store" to listOf("dessert"),
    "bar" to listOf("bar"),
    "bar_and_grill" to listOf("bar"),
    "wine_bar" to listOf("wine"),
    "pub" to listOf("pub"),
    "night_club" to listOf("nightclub"),
    "restaurant" to listOf("restaurant"),
    "meal_takeaway" to listOf("restaurant"),
    "meal_delivery" to listOf("restaurant"),
    "food_court" to listOf("restaurant"),
    "park" to listOf("park"),
    "national_park" to listOf("park"),
    "state_park" to listOf("park"),
    "dog_park" to listOf("park"),
    "garden" to listOf("garden"),
    "botanical_garden" to listOf("botanical"),
    "hiking_area" to listOf("hike"),
    "beach" to listOf("beach"),
    "museum" to listOf("museum"),
    "art_gallery" to listOf("gallery"),
    "church" to listOf("church"),
    "hindu_temple" to listOf("temple"),
    "buddhist_temple" to listOf("temple"),
    "historical_landmark" to listOf("landmark"),
    "monument" to listOf("monument"),
    "observation_deck" to listOf("tower"),
    "tourist_attraction" to listOf("landmark"),
)

/** OpenStreetMap (Nominatim `type`) → the tags it should contribute. */
private val OSM_TYPE_TAGS: Map<String, List<String>> = mapOf(
    "restaurant" to listOf("restaurant"),
    "fast_food" to listOf("restaurant"),
    "food_court" to listOf("restaurant"),
    "cafe" to listOf("cafe"),
    "coffee" to listOf("cafe"),
    "tea" to listOf("tea"),
    "bakery" to listOf("bakery"),
    "pastry" to listOf("pastry"),
    "confectionery" to listOf("dessert"),
    "ice_cream" to listOf("dessert"),
    "chocolate" to listOf("dessert"),
    "bar" to listOf("bar"),
    "pub" to listOf("pub"),
    "biergarten" to listOf("beer"),
    "nightclub" to listOf("nightclub"),
    "wine" to listOf("wine"),
    "park" to listOf("park"),
    "garden" to listOf("garden"),
    "nature_reserve" to listOf("park"),
    "beach" to listOf("beach"),
    "beach_resort" to listOf("beach"),
    "peak" to listOf("peak"),
    "volcano" to listOf("mountain"),
    "water" to listOf("lake"),
    "river" to listOf("river"),
    "museum" to listOf("museum"),
    "gallery" to listOf("gallery"),
    "artwork" to listOf("gallery"),
    "attraction" to listOf("landmark"),
    "place_of_worship" to listOf("church"),
    "monument" to listOf("monument"),
    "memorial" to listOf("monument"),
)

/**
 * OSM `category` values that describe a venue. An unmapped `type` under one of these
 * still becomes a plain tag ("greengrocer", "theatre"); under any other category it is
 * dropped, since values like `boundary`/`place` describe geography, not a place to visit.
 */
private val OSM_VENUE_CATEGORIES = setOf("amenity", "shop", "leisure", "tourism", "natural", "historic")

/**
 * Tags for a Google Places result. [primaryType] is consulted first — a place typed both
 * `cafe` and `bakery` is whichever Google calls primary, not whichever sorts first.
 */
internal fun googleTypeTags(types: List<String>, primaryType: String? = null): List<String> =
    (listOfNotNull(primaryType) + types)
        .flatMap { googleTagsForType(it) }
        .distinct()
        .take(MAX_TAGS)

private fun googleTagsForType(type: String): List<String> {
    val normalized = type.trim().lowercase()
    GOOGLE_TYPE_TAGS[normalized]?.let { return it }
    // `{cuisine}_restaurant` (korean_restaurant, ramen_restaurant, …) is an open-ended
    // family, so it is matched by shape rather than enumerated — and the cuisine word
    // it yields is a genuinely useful tag in its own right.
    val cuisine = normalized.removeSuffix("_restaurant").takeIf { it != normalized }
    return if (cuisine.isNullOrBlank()) emptyList() else listOf(cuisine.replace('_', ' '), "restaurant")
}

/** Tags for a Nominatim result's `category`/`type` pair. */
internal fun osmTypeTags(category: String?, type: String?): List<String> {
    val normalizedType = type?.trim()?.lowercase().orEmpty()
    if (normalizedType.isEmpty()) return emptyList()
    OSM_TYPE_TAGS[normalizedType]?.let { return it.take(MAX_TAGS) }
    val normalizedCategory = category?.trim()?.lowercase().orEmpty()
    return if (normalizedCategory in OSM_VENUE_CATEGORIES) {
        listOf(normalizedType.replace('_', ' '))
    } else {
        emptyList()
    }
}

/**
 * Google `includedTypes` for a query-less nearby browse, per category.
 *
 * The reverse direction of [googleTypeTags]: that one reads Google's vocabulary into HOTPL
 * tags, this one asks Google in its own vocabulary for one category at a time. Without a
 * category the browse returns every kind at once, which reads as noise — a grocery store
 * ranking above the cafe next door.
 */
private val NEARBY_TYPES_BY_CATEGORY: Map<PlaceCategory, List<String>> = mapOf(
    PlaceCategory.CAFE to listOf("cafe", "coffee_shop", "tea_house"),
    PlaceCategory.RESTAURANT to listOf("restaurant"),
    PlaceCategory.BAKERY to listOf("bakery", "dessert_shop", "ice_cream_shop"),
    PlaceCategory.BAR to listOf("bar", "pub", "wine_bar"),
)

/** Every type the browse falls back to when no category is chosen. */
private val ALL_NEARBY_TYPES: List<String> = NEARBY_TYPES_BY_CATEGORY.values.flatten().distinct()

/** Types to ask for; [category] null means "anything the journal is about". */
internal fun googleNearbyTypes(category: PlaceCategory?): List<String> =
    category?.let { NEARBY_TYPES_BY_CATEGORY[it] } ?: ALL_NEARBY_TYPES

/**
 * The categories the Directory offers as a nearby browse, in the order the chips show.
 *
 * Taken from the map's own key order rather than [PlaceCategory.entries]: the enum's order
 * is tag-match priority (cafe beats bakery when a place is tagged both), which has nothing
 * to do with which chip a reader wants first. The outdoor categories the enum also carries
 * (beach, lake, mountain) are deliberately absent — journal classifications, not things to
 * walk to.
 */
val NEARBY_BROWSE_CATEGORIES: List<PlaceCategory> = NEARBY_TYPES_BY_CATEGORY.keys.toList()
